/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { createSlice } from '@reduxjs/toolkit';

interface Cache {
  hasCache: boolean;
  loadedCache: boolean;
}
const initialState: Cache = {
  hasCache: false,
  loadedCache: false
};

const slice = createSlice({
  name: 'locationCache',
  initialState,
  reducers: {
    setLoaded: (state) => {
      state.loadedCache = true;
    }
  }
});

export default slice.reducer;

//
// localStorage budget
//
// Firestore's multi-tab persistence (see src/firebase/firebase.ts) keeps its
// shared client state in localStorage. Once the origin quota is exhausted its
// internal setItem throws QuotaExceededError, which surfaces as
// "Firestore INTERNAL UNHANDLED ERROR: The quota has been exceeded" and takes
// the Firestore client down with it. The analytics payloads cached here used to
// grow without bound (one entry per location per page, never evicted), so they
// were the ones eating the quota. Every entry we write is now namespaced,
// timestamped, size-capped and evicted oldest-first inside a fixed budget that
// leaves the rest of the origin quota to Firestore.
//
const NAMESPACE = 'lvcache:';
const ENTRY_TTL_MS = 24 * 60 * 60 * 1000;
const BUDGET_CHARS = 1_500_000;
const MAX_ENTRY_CHARS = 300_000;

// Key suffixes written by earlier builds of this module, before the namespace
// existed: `${locationSlug}_${getCacheKey(name, href)}`. Swept once per page
// load so upgrading users get their quota back.
const LEGACY_KEY_SUFFIXES = [
  '_Plots',
  '_datatable',
  '_postsList',
  '_shortAnalytics',
  '_topRated',
  '_user_info'
];

interface Envelope<T> {
  t: number;
  v: T;
}

interface StoredEntry {
  key: string;
  chars: number;
  writtenAt: number;
}

function isQuotaExceeded(error: unknown): boolean {
  if (!(error instanceof DOMException)) return false;
  return (
    error.name === 'QuotaExceededError' ||
    error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    error.code === 22 ||
    error.code === 1014
  );
}

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {}
}

function allKeys(): string[] {
  const keys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key != null) keys.push(key);
    }
  } catch {}
  return keys;
}

let legacySweepDone = false;

function sweepLegacyKeys(): void {
  if (legacySweepDone) return;
  legacySweepDone = true;
  allKeys()
    .filter(
      (key) =>
        !key.startsWith(NAMESPACE) &&
        LEGACY_KEY_SUFFIXES.some((suffix) => key.endsWith(suffix))
    )
    .forEach(safeRemoveItem);
}

// Entries are always written as {"t":<epochMs>,"v":...}, so the timestamp can
// be read off the front without parsing megabytes of payload. Anything that
// does not match was not written by this module in its current shape and is
// dropped.
const TIMESTAMP_PREFIX = /^\{"t":(\d+),/;

function readEntries(): StoredEntry[] {
  const entries: StoredEntry[] = [];
  for (const key of allKeys()) {
    if (!key.startsWith(NAMESPACE)) continue;
    const raw = safeGetItem(key);
    if (raw == null) continue;
    const match = TIMESTAMP_PREFIX.exec(raw);
    if (match == null) {
      safeRemoveItem(key);
      continue;
    }
    entries.push({
      key,
      chars: key.length + raw.length,
      writtenAt: Number(match[1])
    });
  }
  return entries;
}

// Drops expired entries, then evicts oldest-first until `cost` more characters
// fit inside the budget. `replacing` is about to be overwritten, so it never
// counts towards the total.
function makeRoom(cost: number, replacing: string): void {
  const now = Date.now();
  const live: StoredEntry[] = [];
  for (const entry of readEntries()) {
    if (entry.key === replacing || now - entry.writtenAt > ENTRY_TTL_MS) {
      safeRemoveItem(entry.key);
      continue;
    }
    live.push(entry);
  }

  let used = live.reduce((sum, entry) => sum + entry.chars, 0);
  live.sort((a, b) => a.writtenAt - b.writtenAt);
  for (const entry of live) {
    if (used + cost <= BUDGET_CHARS) return;
    safeRemoveItem(entry.key);
    used -= entry.chars;
  }
}

function trySetItem(key: string, payload: string): boolean {
  try {
    localStorage.setItem(key, payload);
    return true;
  } catch (error) {
    if (!isQuotaExceeded(error)) throw error;
    return false;
  }
}

function writeEntry<T>(key: string, data: T): void {
  sweepLegacyKeys();
  const fullKey = `${NAMESPACE}${key}`;

  let payload: string;
  try {
    const envelope: Envelope<T> = { t: Date.now(), v: data };
    payload = JSON.stringify(envelope);
  } catch {
    return;
  }
  if (payload == null) return;

  const cost = fullKey.length + payload.length;
  if (cost > MAX_ENTRY_CHARS) {
    // Too big to be worth a cache slot — a refetch is cheaper than the quota.
    safeRemoveItem(fullKey);
    return;
  }

  makeRoom(cost, fullKey);
  if (trySetItem(fullKey, payload)) return;

  // Still full: something outside our budget (another tab, Firestore, an
  // extension) owns the space. Give up the whole cache and try once more.
  readEntries().forEach((entry) => safeRemoveItem(entry.key));
  if (!trySetItem(fullKey, payload)) safeRemoveItem(fullKey);
}

function readEntry<T>(key: string): T | null {
  sweepLegacyKeys();
  const fullKey = `${NAMESPACE}${key}`;
  const raw = safeGetItem(fullKey);
  if (raw == null) return null;

  let envelope: Envelope<T> | null = null;
  try {
    envelope = JSON.parse(raw) as Envelope<T>;
  } catch {}
  // The `typeof envelope !== 'object'` guard keeps the `in` operator below off
  // primitives (JSON.parse('42') and friends) rather than relying on the
  // short-circuit order to stay non-throwing.
  if (
    envelope == null ||
    typeof envelope !== 'object' ||
    typeof envelope.t !== 'number' ||
    !('v' in envelope)
  ) {
    safeRemoveItem(fullKey);
    return null;
  }
  if (Date.now() - envelope.t > ENTRY_TTL_MS) {
    safeRemoveItem(fullKey);
    return null;
  }
  return envelope.v;
}

export function readLocationCache<T>(
  locationSlug: string,
  key: string
): T | null {
  return readEntry<T>(`${locationSlug}_${key}`);
}

export function writeLocationCache<T>(
  locationSlug: string,
  key: string,
  data: T
): void {
  writeEntry(`${locationSlug}_${key}`, data);
}

export function writeUrlCache<T>(url: string, data: T): void {
  writeEntry(url, data);
}

export function ReadUrlCache<T>(url: string): T | null {
  return readEntry<T>(url);
}

export function updateLocationCache<T>(
  locationSlug: string,
  key: string,
  data: T
): void {
  const cached = readEntry<Record<string, unknown>>(`${locationSlug}_${key}`);
  if (cached) writeEntry(`${locationSlug}_${key}`, { ...cached, ...data });
}
